import { useEffect, useRef } from "react";

const VIOLET = "118, 87, 232";
const TEAL = "32, 184, 173";

export const HeroArt = () => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const ctx = canvas.getContext("2d");
    let raf, w, h, mouse = { x: 0.5, y: 0.5 };
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const r = canvas.parentElement.getBoundingClientRect();
      w = r.width; h = r.height;
      canvas.width = w * dpr; canvas.height = h * dpr;
      canvas.style.width = `${w}px`; canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const N = 64;
    const pts = Array.from({ length: N }, () => ({
      x: Math.random(), y: Math.random(),
      vx: (Math.random() - 0.5) * 0.0006, vy: (Math.random() - 0.5) * 0.0006,
      r: Math.random() * 1.6 + 0.6, c: Math.random() > 0.55 ? VIOLET : TEAL,
    }));

    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      mouse = { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
    };
    window.addEventListener("pointermove", onMove);

    let t = 0;
    const draw = () => {
      t += 0.004;
      ctx.clearRect(0, 0, w, h);
      const px = (mouse.x - 0.5) * 18, py = (mouse.y - 0.5) * 18;

      for (const p of pts) {
        if (!reduced) {
          p.x += p.vx; p.y += p.vy;
          if (p.x < 0 || p.x > 1) p.vx *= -1;
          if (p.y < 0 || p.y > 1) p.vy *= -1;
        }
      }
      for (let i = 0; i < N; i++) {
        for (let j = i + 1; j < N; j++) {
          const a = pts[i], b = pts[j];
          const dx = (a.x - b.x) * w, dy = (a.y - b.y) * h;
          const d = Math.hypot(dx, dy);
          if (d < w * 0.14) {
            const o = (1 - d / (w * 0.14)) * 0.28;
            ctx.strokeStyle = `rgba(${a.c},${o})`;
            ctx.lineWidth = 0.7;
            ctx.beginPath();
            ctx.moveTo(a.x * w + px * a.r * 0.4, a.y * h + py * a.r * 0.4);
            ctx.lineTo(b.x * w + px * b.r * 0.4, b.y * h + py * b.r * 0.4);
            ctx.stroke();
          }
        }
      }
      for (const p of pts) {
        const pulse = reduced ? 1 : 0.7 + Math.sin(t * 3 + p.r * 10) * 0.3;
        ctx.fillStyle = `rgba(${p.c},${0.75 * pulse})`;
        ctx.beginPath();
        ctx.arc(p.x * w + px * p.r * 0.4, p.y * h + py * p.r * 0.4, p.r * pulse, 0, Math.PI * 2);
        ctx.fill();
      }
      if (!reduced) raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointermove", onMove);
    };
  }, []);

  return (
    <div className="relative h-full w-full" data-testid="hero-artwork" aria-hidden="true">
      <div className="absolute inset-0 rounded-[2rem] border border-white/[0.06] bg-gradient-to-br from-deep/80 via-midnight to-midnight" />
      <canvas ref={ref} className="absolute inset-0" />
      <div className="absolute inset-0 flex items-center justify-center">
        <svg viewBox="0 0 220 220" className="w-[52%] max-w-[340px] animate-float-slow opacity-90">
          <defs>
            <linearGradient id="evGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#7657E8" />
              <stop offset="100%" stopColor="#20B8AD" />
            </linearGradient>
          </defs>
          <g fill="none" stroke="url(#evGrad)" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M86 44 H38 V176 H86" />
            <path d="M38 110 H74" />
            <path d="M104 44 L143 176 L182 44" />
          </g>
          <circle cx="143" cy="176" r="6" fill="#20B8AD" />
        </svg>
      </div>
      <div className="pointer-events-none absolute -inset-10 -z-10 rounded-full bg-violet/20 blur-[110px]" />
    </div>
  );
};
